import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import bgVideo from "../assets/bg.mp4";

const emptyForm = {
  title: "",
  message: "",
  is_published: true,
};

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [undoAnnouncementId, setUndoAnnouncementId] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/announcements");
      setAnnouncements(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setAnnouncements([]);
      setToast({ type: "error", message: "Failed to load announcements." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timeout);
  }, [toast]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingId) {
        await api.put(`/admin/announcements/${editingId}`, formData);
        setToast({ type: "success", message: "Announcement updated." });
      } else {
        await api.post("/admin/announcements", formData);
        setToast({ type: "success", message: "Announcement created." });
      }

      resetForm();
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Failed to save announcement." });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title || "",
      message: announcement.message || "",
      is_published: Boolean(announcement.is_published),
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this announcement?")) return;

    const category = window
      .prompt(
        "Archive category (Resolved, Duplicate, Invalid, Policy, Other)",
        "Resolved"
      )
      ?.trim();
    if (!category) return;

    const allowed = ["Resolved", "Duplicate", "Invalid", "Policy", "Other"];
    if (!allowed.includes(category)) {
      setToast({ type: "error", message: "Invalid archive category." });
      return;
    }

    const note = window.prompt("Archive note", "Completed / no longer needed")?.trim();
    if (!note || note.length < 3) {
      setToast({ type: "error", message: "Archive note is required." });
      return;
    }

    try {
      await api.delete(`/admin/announcements/${id}`, {
        data: {
          archive_reason_category: category,
          archive_reason_note: note,
        },
      });
      setAnnouncements((prev) => prev.filter((item) => item.id !== id));
      setUndoAnnouncementId(id);
      setTimeout(() => setUndoAnnouncementId(null), 8000);
      setToast({ type: "success", message: "Announcement archived. Undo available for 8 seconds." });
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Failed to archive announcement." });
    }
  };

  const handleUndoArchive = async () => {
    if (!undoAnnouncementId) return;
    try {
      await api.put(`/admin/announcements/${undoAnnouncementId}/restore`);
      setUndoAnnouncementId(null);
      fetchAnnouncements();
      setToast({ type: "success", message: "Archive undone. Announcement restored." });
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Failed to undo archive." });
    }
  };

  const filteredAnnouncements = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return announcements;

    return announcements.filter((item) =>
      [item.title, item.message, item.is_published ? "published" : "draft"]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [announcements, searchTerm]);

  const publishedCount = announcements.filter((item) => item.is_published).length;
  const draftCount = announcements.length - publishedCount;
  const inputClass =
    "w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        src={bgVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-slate-950/85"></div>

      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
            toast.type === "success" ? "bg-teal-600" : "bg-red-600"
          }`}
        >
          <div className="flex items-center gap-3">
            <span>{toast.message}</span>
            {undoAnnouncementId && (
              <button
                onClick={handleUndoArchive}
                className="rounded border border-white/30 px-2 py-1 text-xs font-semibold hover:bg-white/10"
              >
                Undo
              </button>
            )}
          </div>
        </div>
      )}

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 text-white sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Announcements
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              Announcement Management
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Create notices for tenants, publish updates, and manage drafts.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center sm:min-w-[360px]">
            {[
              ["Total", announcements.length, "text-white"],
              ["Published", publishedCount, "text-teal-200"],
              ["Drafts", draftCount, "text-amber-200"],
            ].map(([label, value, color]) => (
              <div
                key={label}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-3"
              >
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl"
          >
            <div className="mb-5 border-b border-white/10 pb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                {editingId ? "Editing" : "New Announcement"}
              </p>
              <h2 className="mt-2 text-xl font-bold text-white">
                {editingId ? "Update Announcement" : "Create Announcement"}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="adminannouncements-field-1" className="mb-2 block text-sm font-semibold text-slate-200">
                  Title
                </label>
                <input id="adminannouncements-field-1"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className={inputClass}
                  placeholder="Announcement title"
                  required
                />
              </div>

              <div>
                <label htmlFor="adminannouncements-field-2" className="mb-2 block text-sm font-semibold text-slate-200">
                  Message
                </label>
                <textarea id="adminannouncements-field-2"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, message: e.target.value }))
                  }
                  rows={7}
                  className={`${inputClass} resize-none`}
                  placeholder="Write the announcement details"
                  required
                />
              </div>

              <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm font-semibold text-slate-200">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_published: e.target.checked,
                    }))
                  }
                />
                Publish to tenants
              </label>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-lg bg-teal-600 py-3 font-semibold text-white transition hover:bg-teal-500 disabled:opacity-60"
              >
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 rounded-lg border border-white/15 py-3 font-semibold text-slate-100 transition hover:bg-white/10"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <section>
            <div className="mb-4 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputClass}
                placeholder="Search announcements"
              />
            </div>

            {loading ? (
              <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
                Loading announcements...
              </div>
            ) : filteredAnnouncements.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
                No announcements found.
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredAnnouncements.map((announcement) => (
                  <article
                    key={announcement.id}
                    className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {announcement.title}
                        </h3>
                        <p className="mt-1 text-xs text-slate-400">
                          {new Date(announcement.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span
                        className={`w-fit rounded-full border px-3 py-1 text-xs font-semibold ${
                          announcement.is_published
                            ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                            : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                        }`}
                      >
                        {announcement.is_published ? "Published" : "Draft"}
                      </span>
                    </div>

                    <p className="mt-4 whitespace-pre-wrap rounded-lg bg-white/5 p-4 text-sm leading-6 text-slate-300">
                      {announcement.message}
                    </p>

                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => startEdit(announcement)}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="rounded-lg border border-red-300/30 bg-red-500/15 px-4 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
